/**
 * The open chat panel — anchored bottom-right by the parent widget.
 * Renders the active-context chip, message list, in-flight tool calls,
 * and the composer.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { buildActiveContext } from './context';
import { usePortalChatStore } from './store';
import { useChatStream } from './useChatStream';
import ChatChart from './ChatChart';
import { isChartSpec } from './chartSpec';
import MessageBubble from './MessageBubble';
import ToolCallsDisclosure from './ToolCallsDisclosure';

// Default quick prompts — cover the main question families so a new
// user can see what the assistant can do with a single click each.
// Each one is engineered to either exercise the headline playbook
// (allocation moves), the event-attribution playbook (market shocks),
// or to surface one of the chart families.
const QUICK_PROMPTS = [
  {
    label: "Why has equity exposure moved this week?",
    text: "Why has the equity exposure moved this week?",
  },
  {
    label: "Today's allocation across all mandates",
    text: "What's today's allocation across all four mandates, and where do they differ the most?",
  },
  {
    label: "Decompose this week's bond ER move",
    text: "Decompose this week's expected-return move for bonds in FQM by state variable, and show the term structure shift.",
  },
  {
    label: "What hit the market this month?",
    text: "What macro shocks hit the market over the past month? Plot the dominant variable.",
  },
  {
    label: "Latest GDP and inflation outlook",
    text: "How are the macro forecasts looking right now? Show me the GDP and inflation fans.",
  },
  {
    label: "FQM exposures and vols, past 6 months",
    text: "How have FQM exposures and the asset-class vol forecasts evolved over the past 6 months? Plot both.",
  },
];

export default function ChatPanel({ onClose }: { onClose: () => void }): JSX.Element {
  const location = useLocation();
  const ctx = buildActiveContext(location.pathname);

  const {
    messages, draft, setDraft, inFlight, error, reset,
    panelWidth, panelHeight, setPanelSize,
    fund, setFund,
  } = usePortalChatStore();
  const { send, cancel } = useChatStream();

  // Drag-resize from the top-left corner. The panel is anchored
  // bottom-right (fixed bottom-4 right-4), so growing means dragging
  // the grip toward the top-left.
  const onResizeStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      const startW = panelWidth;
      const startH = panelHeight;
      const MIN_W = 320, MIN_H = 360;
      const MAX_W = window.innerWidth - 32;
      const MAX_H = window.innerHeight - 32;
      const onMove = (ev: MouseEvent) => {
        const w = Math.min(MAX_W, Math.max(MIN_W, startW + (startX - ev.clientX)));
        const h = Math.min(MAX_H, Math.max(MIN_H, startH + (startY - ev.clientY)));
        setPanelSize(w, h);
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [panelWidth, panelHeight, setPanelSize],
  );

  // Auto-scroll only when the user is already near the bottom. If they
  // scrolled up to read earlier content, leave their viewport alone.
  const tailRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef<boolean>(true);

  const onScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distance < 48;
  }, []);

  useEffect(() => {
    if (stickToBottomRef.current) {
      tailRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  function submit(text?: string) {
    const t = text ?? draft;
    setDraft('');
    void send(t);
  }

  return (
    <div
      className="fixed bottom-4 right-4 z-[60] flex flex-col rounded-xl border border-gray-200 bg-[color:var(--light-bg,#F9FAFB)] shadow-xl"
      style={{ width: `${panelWidth}px`, height: `${panelHeight}px` }}
    >
      {/* Top-left resize grip — drag toward the top-left to enlarge. */}
      <div
        onMouseDown={onResizeStart}
        title="Drag to resize"
        className="absolute -top-1 -left-1 z-10 h-3 w-3 cursor-nwse-resize rounded-full border border-gray-300 bg-white hover:bg-gray-200"
        aria-label="Resize chat panel"
      />
      {/* Header */}
      <header className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-800">MARS Assistant</span>
          {/* Fund toggle. Drives the agent's ACTIVE_CONTEXT.fund. */}
          <select
            value={fund}
            onChange={(e) => setFund(e.target.value as typeof fund)}
            title="Fund the assistant reasons about"
            className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[0.72rem] text-gray-700 hover:border-gray-300 focus:border-accent focus:outline-none"
          >
            <option value="mars-fqm">FQM</option>
            <option value="mars-dar-nn">DAR NN</option>
            <option value="mars-fidelity-balanced">Balanced</option>
            <option value="mars-fidelity-conservative">Conservative</option>
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={reset}
            className="rounded px-2 py-1 text-[0.78rem] text-gray-600 hover:bg-gray-100"
            title="New conversation"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={onClose}
            // Bigger hit area: 32px square. The "×" is rendered at
            // 1.4rem with a tight line-height so it sits centred.
            className="flex h-8 w-8 items-center justify-center rounded-full text-[1.4rem] leading-none text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            aria-label="Close chat"
            title="Close chat"
          >
            ×
          </button>
        </div>
      </header>

      {/* Messages */}
      <div ref={scrollerRef} onScroll={onScroll} className="flex-1 overflow-y-auto px-3 py-2">
        {messages.length === 0 && (
          <div className="mt-2 space-y-1.5">
            <p className="text-[0.8rem] text-gray-600">
              Ask about exposures, expected returns, or the term-structure decomposition.
            </p>
            {QUICK_PROMPTS.map((q) => (
              <button
                key={q.label}
                type="button"
                onClick={() => submit(q.text)}
                className="block w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-left text-[0.8rem] text-gray-700 hover:bg-gray-50"
              >
                {q.label}
              </button>
            ))}
          </div>
        )}

        {messages.map((m, i) => {
          const calls = m.role === 'assistant' ? (m.toolCalls ?? []) : [];
          const isLast = i === messages.length - 1;
          const chartCalls = calls.filter(
            (c) => c.name === 'propose_chart' && isChartSpec(c.output),
          );
          return (
            <div key={i}>
              <MessageBubble message={m} />
              {/* Charts render right below the assistant text, where the
                  agent produced them — not at the bottom of the panel. */}
              {chartCalls.map((c) => (
                <ChatChart
                  key={`chart-${c.id}`}
                  spec={c.output as unknown as Parameters<typeof ChatChart>[0]['spec']}
                />
              ))}
              {/* Tool-call pill (collapsed by default) under the chart. */}
              {calls.length > 0 && (
                <ToolCallsDisclosure
                  calls={calls}
                  inFlight={isLast && inFlight}
                />
              )}
            </div>
          );
        })}

        {error && (
          <div className="my-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-[0.78rem] text-red-700">
            {error}
          </div>
        )}
        <div ref={tailRef} />
      </div>

      {/* Composer */}
      <form
        className="flex gap-1.5 border-t border-gray-200 px-3 py-2"
        onSubmit={(e) => { e.preventDefault(); submit(); }}
      >
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          rows={2}
          placeholder={ctx ? 'Ask about this page…' : 'Open a MARS page to chat'}
          disabled={!ctx || inFlight}
          className="flex-1 resize-none rounded border border-gray-200 bg-white px-2 py-1.5 text-[0.82rem] focus:border-accent focus:outline-none disabled:bg-gray-50"
        />
        {inFlight ? (
          <button
            type="button"
            onClick={cancel}
            className="rounded bg-gray-200 px-3 text-[0.8rem] font-medium text-gray-700 hover:bg-gray-300"
          >
            Stop
          </button>
        ) : (
          <button
            type="submit"
            disabled={!ctx || !draft.trim()}
            // Use the explicit CSS variable; the `bg-accent` alias was
            // rendering faint, hiding the "Send" label against it.
            className="rounded px-3 text-[0.8rem] font-medium text-white bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] disabled:bg-gray-300 disabled:text-gray-500"
          >
            Send
          </button>
        )}
      </form>
    </div>
  );
}
